import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { Callout, DataTable, DescriptionList, CATEGORY_HEX, SERIES_TONES, type Column } from '../../components/ds';

/**
 * Data visualization — the standard, and what it is a standard FOR.
 *
 * The previous page documented conventions for a charting system that was
 * never identified. The register's first pass then claimed there was no
 * charting component in the tree at all; that was wrong. recharts is a
 * dependency and is used in seven files.
 *
 * The real gap is narrower and more actionable: five of those seven files
 * never read the theme, and axis/grid/tooltip styling is re-decided per file
 * in hard hex — so charts do not follow light/dark, and two charts on one page
 * can disagree about what a gridline looks like. ds/chartTheme closes that.
 */

interface Rule {
  rule: string;
  why: string;
}

const RULES: Rule[] = [
  {
    rule: 'One accent per chart',
    why: 'The series the reader is meant to land on takes the accent; everything else is neutral. A chart where every series is a different bright colour has no focus, so the reader supplies their own.',
  },
  {
    rule: 'Horizontal gridlines only',
    why: 'Vertical gridlines on a time axis are lines the reader has to look past to see the shape. chartTheme().grid sets vertical: false.',
  },
  {
    rule: 'Hairline axes, no tick marks',
    why: 'The axis is a reference, not content. Tick marks add visual noise proportional to the number of ticks.',
  },
  {
    rule: 'Emphasise the endpoint',
    why: 'On a trend, the last value is usually the answer. A filled dot there does more than a legend.',
  },
  {
    rule: 'Mono for every number',
    why: 'Tick labels, tooltips and legends are data. Mono gives them tabular figures so digits line up between ticks.',
  },
  {
    rule: 'Never a gradient fill',
    why: 'A gradient encodes a second dimension that does not exist. Use a flat fill, or an area at low opacity.',
  },
  {
    rule: 'A chart needing a sixth colour is a chart to redesign',
    why: 'Group the tail into “Other”. SERIES_TONES stops at five on purpose.',
  },
];

const RULE_COLUMNS: Column<Rule>[] = [
  { key: 'rule', header: 'Rule', width: '30%', render: (r) => (
    <span className="text-[var(--text-pri)]">{r.rule}</span>
  ) },
  { key: 'why', header: 'Why' },
];

export const PrioritiesAIDataVizPage: React.FC = () => (
  <>
    <ProductDesignPageHeader
      title="Data visualization"
      eyebrow="Charts · one standard"
      subtitle={
        <>
          <code className="font-mono text-[0.85em] text-[var(--mustard)]">recharts</code> is the
          charting library and is already used in seven files. What was missing is a{' '}
          <strong className="text-[var(--text-pri)]">theme</strong> — five of those seven never read
          the theme at all, so their charts do not follow light and dark.
        </>
      }
    />

    <ProductDesignSection
      title="Use the shared theme"
      hint="chartTheme() resolves the tokens to concrete colours and returns spreadable recharts props."
    >
      <pre
        className="overflow-x-auto border bg-[var(--bg-raised)] p-4 font-mono text-[13px] leading-relaxed text-[var(--text-sec)]"
        style={{ borderColor: 'var(--rule)' }}
      >
{`import { chartTheme, seriesColor } from '@/components/ds';

const t = chartTheme();

<LineChart data={data}>
  <CartesianGrid {...t.grid} />
  <XAxis dataKey="cycle" {...t.axis} />
  <YAxis {...t.axis} />
  <Tooltip {...t.tooltip} />
  <Line dataKey="ratified" stroke={seriesColor(0)} dot={false} />
</LineChart>`}
      </pre>

      <Callout
        tone="mustard"
        label="Why tokens are resolved, not passed through"
        className="mt-5"
      >
        recharts writes most colours to SVG presentation attributes.{' '}
        <code className="font-mono text-[0.85em]">var(--green)</code> does resolve there in current
        browsers, but not across every path this app uses — PDF export renders through a detached
        document where the custom property is not in scope, and the series would come out black.
        Resolving to a concrete value at render time works everywhere and costs one{' '}
        <code className="font-mono text-[0.85em]">getComputedStyle</code> call.
      </Callout>
    </ProductDesignSection>

    <ProductDesignSection
      title="Rules"
      hint="The visual register is the one the investor deck sets: hairline axes, one accent, nothing competing with the data."
    >
      <DataTable
        columns={RULE_COLUMNS}
        rows={RULES}
        getRowKey={(r) => r.rule}
        density="comfortable"
        caption="Chart rules"
        captionHidden
      />
    </ProductDesignSection>

    <ProductDesignSection
      title="Two palettes, two jobs"
      hint="Getting this wrong is the most common charting mistake here — using an arbitrary series colour for something that carries domain meaning."
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <p className="mb-3 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
            Series — no domain meaning
          </p>
          <div className="mb-3 flex flex-wrap gap-2">
            {SERIES_TONES.map((tone, i) => (
              <span key={tone} className="inline-flex items-center gap-2">
                <span
                  className="inline-block h-4 w-4"
                  style={{ backgroundColor: tone === 'neutral' ? 'var(--text-dim)' : `var(--${tone})` }}
                />
                <span className="font-mono text-[13px] text-[var(--text-ter)]">{i}</span>
              </span>
            ))}
          </div>
          <p className="text-sm leading-relaxed text-[var(--text-sec)]">
            <code className="font-mono text-[0.85em]">seriesColor(i)</code> — for series whose order
            is arbitrary. Stops at five deliberately.
          </p>
        </div>

        <div>
          <p className="mb-3 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
            Item category — carries meaning
          </p>
          <div className="mb-3 flex flex-wrap gap-3">
            {Object.entries(CATEGORY_HEX).map(([name, hex]) => (
              <span key={name} className="inline-flex items-center gap-1.5">
                <span className="inline-block h-4 w-4" style={{ backgroundColor: hex }} />
                <span className="font-mono text-[13px] text-[var(--text-ter)]">{name}</span>
              </span>
            ))}
          </div>
          <p className="text-sm leading-relaxed text-[var(--text-sec)]">
            <code className="font-mono text-[0.85em]">CATEGORY_HEX</code> from{' '}
            <code className="font-mono text-[0.85em]">@/lib/itemCategoryColors</code> — the same
            encoding the item list and grid-cell picker use, so a category is the same colour
            wherever it appears. Never substitute a series colour here.
          </p>
        </div>
      </div>
    </ProductDesignSection>

    <ProductDesignSection title="Where charts are today" hint="Measured, so the migration has a finish line.">
      <DescriptionList
        columns={3}
        items={[
          { label: 'Library', value: 'recharts' },
          { label: 'Files charting', value: '7' },
          { label: 'Reading the theme', value: <span className="text-[var(--coral)]">2 of 7</span> },
        ]}
      />
      <p className="mt-4 text-sm leading-relaxed text-[var(--text-sec)]">
        The five theme-blind files are <code className="font-mono text-[0.85em]">AscMatrix</code>,{' '}
        <code className="font-mono text-[0.85em]">MatrixToolResultsCharts</code>,{' '}
        <code className="font-mono text-[0.85em]">ItemReadinessReport</code>,{' '}
        <code className="font-mono text-[0.85em]">CycleExecutionReport</code> and{' '}
        <code className="font-mono text-[0.85em]">SessionReports</code>. Migrating one is small —
        replace its axis/grid/tooltip props with a <code className="font-mono text-[0.85em]">chartTheme()</code>{' '}
        spread and its hex series colours with <code className="font-mono text-[0.85em]">seriesColor()</code>.
      </p>
    </ProductDesignSection>
  </>
);
